import React, { useState } from "react";
import { NavLink, useHistory, useLocation } from "react-router-dom";
import { InputGroup, Button } from "react-bootstrap";
import { Typeahead } from "react-bootstrap-typeahead";
import { ThemeProvider } from "@mui/material/styles";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import ListItemIcon from "@mui/material/ListItemIcon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import CodeRoundedIcon from "@mui/icons-material/CodeRounded";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import ExitToAppRoundedIcon from "@mui/icons-material/ExitToAppRounded";
import WorkspacesRoundedIcon from "@mui/icons-material/WorkspacesRounded";
import HubRoundedIcon from "@mui/icons-material/HubRounded";
import KeyRoundedIcon from "@mui/icons-material/KeyRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import StorageRoundedIcon from "@mui/icons-material/StorageRounded";
import { getShortName } from "../utils/accessMapFunctions";
import { recordSignOut, workspacesApi } from "../actions";
import { canOpenConsole, canPlatform, manageableWorkspaces, memberWorkspaces, showsLibraryName } from "../utils/permissions";
import {
  currentViewId,
  currentWorkspace,
  hasSeveralWorkspaces,
  switcherOptions,
  switchView,
  switchWorkspace,
  workspacesOf,
} from "../utils/currentWorkspace";
import { ConfirmDialog } from "./workspaces/WorkspaceUi";
import switchDatabase from "../utils/switchDatabase";
import useBranding from "../utils/branding";
import { useViewConfig } from "./app/ViewConfigContext";
import useQuickSearch from "./app/useQuickSearch";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import businessFlowsTheme, { brand } from "../theme/businessFlowsTheme";
import { SHOW_DEVELOPER_API, SHOW_PLANS } from "../features";
import readFeatures from "../utils/featureFlags";
import { NO_VIEW, viewContextLine, viewLine, viewName } from "../utils/viewContext";
import "../index.css";
import "./auth/auth.css";

const PLAN_NAMES = { trial: "Trial", ultimate: "Ultimate", ultimatePlus: "Ultimate Plus", expired: "Expired" };

// Database names travel through URLs and stored values, where the case may
// differ from the server's spelling.
const sameDatabase = (a, b) => String(a || "").toLowerCase() === String(b || "").toLowerCase();

// Which view this person reads the current database through. Lives in
// utils/viewContext.js now, so Home and the account menu say the same;
// re-exported because the bar is where it was first read.
export { viewLine };

const initialsOf = (user, email) =>
  [user?.firstName, user?.lastName]
    .filter(Boolean)
    .map((n) => n.trim()[0])
    .join("")
    .toUpperCase() || (email || "?")[0].toUpperCase();

const Logo = ({ to }) => {
  const branding = useBranding();
  return (
    <NavLink to={to} className="bf-nav__logo" aria-label={`${branding.productName} home`}>
      <img src={branding.logoUrl} alt={branding.productName} />
    </NavLink>
  );
};

const Navbar = (props) => {
  const location = useLocation();

  if (!(props.userEmail && props.user.mainDatabase)) {
    return (
      <header className="bf-nav">
        <div className="bf-nav__inner">
          <Logo to="/" />
        </div>
      </header>
    );
  }

  const search = location.pathname.includes("object") ? (
    <RelationshipExplorerSearch {...props} />
  ) : location.pathname.includes("advance-search") ? null : (
    <ExtendedSearch {...props} />
  );

  return (
    <ThemeProvider theme={businessFlowsTheme}>
      <header className="bf-nav bfx">
        <div className="bf-nav__inner">
          <Logo to="/home" />
          <div className="bf-nav__search">{search}</div>
          <div className="bf-nav__end">
            <DatabaseSwitcher
              current={props.path.substring(1)}
              user={props.user}
              // Views where this person holds any, databases where they hold
              // none - the chosen workspace's, never another's.
              options={switcherOptions(props.user)}
              labels={props.user.databaseLabels}
              outage={props.databaseOutage}
            />
            <AccountMenu {...props} />
          </div>
        </div>
      </header>
    </ThemeProvider>
  );
};

// What the reader is looking at, and what else they could look at.
//
// A view belongs to one database and a person may hold several - two on the
// same database included (product owner, 28 Sep 2026, item 7). So where they
// hold views the list is of **views**, each with its library under it, and
// choosing one takes the library with it. A database no view of theirs covers
// - staff, or a workspace admin reading their own library - stays a plain
// entry, saying in as many words that nothing is laid over it.
//
// **The chip names the view, and the library only for the people the console
// opens for** (product owner, 29 Sep 2026: "don't show database/library name
// to the public - only show workspace and view name"). A library's name is
// ours, not the reader's; the view is what says what they can see, and the
// workspace is named in the menu below it. `showsLibraryName` is the one test
// (utils/permissions.js), so the bar, the account menu and Home cannot
// disagree about who is told what.
//
// Where a reader holds no view at all - a workspace that has never restricted
// anyone, where every member reads every library it owns - there is no name to
// put in its place, so the library's own name is what the chip says. Nothing
// empties and switching keeps working; giving people views is what takes the
// name off the bar, which is the direction anyway.
//
// The menu names the workspace it is listing, and ends with the others.
// The product owner added two databases to bpExperts Cloud, then asked why he
// still couldn't switch: he was reading in bpExperts Internal, which gives him
// one. The server was right and the menu said nothing about it, so a list of
// one read as "switching is broken" rather than "this workspace has one
// library". Naming the workspace, and offering the way to another, is the one
// place that can answer it - and it stays one control in the bar.
//
// `outage` is set when one database can't be reached: it is marked here, so
// the person can see which one the problem is and switch to another.
function DatabaseSwitcher({ current, user, options: given, labels = {}, outage = null }) {
  const nameFor = (db) => labels?.[db] || db;
  const history = useHistory();
  const viewConfig = useViewConfig();
  const view = viewLine(viewConfig?.view);
  // Who is told a library's name, and what everyone else is told instead.
  const showsLibrary = showsLibraryName(user);
  const viewNow = viewName(viewConfig?.view);
  // What a reader is standing in: the view where they hold one, the library
  // where the reader is entitled to its name, and the library as a fallback
  // for someone who holds neither - see the note above.
  const standingIn = (db) => (showsLibrary ? nameFor(db) : viewNow || nameFor(db));
  // An entry in the list, under the same rule: a view by its own name, a
  // library by its name only where this reader is shown one.
  const entryTitle = (option) => (option.viewId ? option.viewName || "View" : nameFor(option.database));
  const [anchor, setAnchor] = useState(null);
  const chosenView = currentViewId();
  // The library open now is always offered, even where the profile arrived
  // without it: a reader must be able to read where they are.
  const list = Array.isArray(given) ? given : [];
  const options = list.some((option) => sameDatabase(option.database, current))
    ? list
    : [...list, { key: `db:${current}`, viewId: null, viewName: null, database: current }];
  const offersViews = options.some((option) => option.viewId);
  const workspace = currentWorkspace(user);
  const elsewhere = workspacesOf(user).filter((one) => one.id !== workspace?.id);
  const canSwitch = options.length > 1 || elsewhere.length > 0;
  const isOffline = (db) => Boolean(outage && outage.database === db);
  const currentOffline = isOffline(current);
  // Where you are: this library, read through this view. With several views
  // on the library and none chosen yet, nothing is ticked - the server is
  // merging them, and choosing one is exactly what this menu is for.
  const isCurrent = (option) => sameDatabase(option.database, current) && (option.viewId || "") === chosenView;

  return (
    <>
      {/* The name in the chip is cut where the bar is narrow, so the tooltip
          always carries it in full - this is the only database switcher in the
          product, and a reader must be able to read which database they are in. */}
      <Tooltip
        title={
          <>
            {currentOffline
              ? outage.msg
              : canSwitch
              ? `${standingIn(current)} - switch ${offersViews ? "view" : "database"}`
              : `${standingIn(current)} - connected database`}
            {view && !currentOffline && showsLibrary && (
              <Box component="span" sx={{ display: "block", opacity: 0.8 }}>
                {view}
              </Box>
            )}
          </>
        }
      >
        <Chip
          className="bf-nav__db"
          size="small"
          icon={<StorageRoundedIcon />}
          label={currentOffline ? `${standingIn(current)} - offline` : standingIn(current)}
          color={currentOffline ? "warning" : undefined}
          variant="outlined"
          onClick={canSwitch ? (e) => setAnchor(e.currentTarget) : undefined}
          onDelete={canSwitch ? (e) => setAnchor(e.currentTarget.closest(".MuiChip-root")) : undefined}
          deleteIcon={canSwitch ? <ExpandMoreRoundedIcon /> : undefined}
          aria-haspopup={canSwitch ? "menu" : undefined}
        />
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ className: "bfx", sx: { mt: 1, minWidth: 220, maxHeight: 360, borderRadius: 3, border: `1px solid ${brand.line}` } }}
      >
        {/* The workspace being listed. Without it, one entry reads as a
            broken switcher rather than as a workspace with one library. */}
        <Box sx={{ px: 2, pt: 0.75, pb: 0.5 }}>
          {workspace?.name && (
            <Typography variant="caption" sx={{ display: "block", fontWeight: 700, color: brand.ink, overflowWrap: "anywhere" }}>
              {workspace.name}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontWeight: 600 }}>
            {offersViews ? "Switch view" : "Switch database"}
          </Typography>
        </Box>
        {options.map((option) => {
          const here = isCurrent(option);
          // A view is named by its own name; a library nothing of theirs
          // narrows is named by the library, and says so in the words every
          // other screen uses.
          const title = entryTitle(option);
          // Under it, the library it belongs to - but only for a reader who
          // is shown library names at all. For everyone else two views on one
          // library are told apart by their own names, which is what a view
          // is named for. A plain library entry says, in the words every
          // other screen uses, that nothing is laid over it.
          const under = option.viewId ? (showsLibrary ? nameFor(option.database) : "") : NO_VIEW;
          return (
            <MenuItem
              key={option.key}
              selected={here}
              onClick={() => {
                setAnchor(null);
                if (!here) switchView(user, option, current, history, switchDatabase);
              }}
            >
              <ListItemIcon>{here ? <CheckRoundedIcon fontSize="small" /> : null}</ListItemIcon>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: here ? 700 : 500 }}>
                  {title}
                  {isOffline(option.database) ? " - offline" : ""}
                </Typography>
                {under && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", overflowWrap: "anywhere" }}>
                    {under}
                  </Typography>
                )}
              </Box>
            </MenuItem>
          );
        })}
        {/* ...and where they are in more than one workspace, the way on -
            so the list never dead-ends on "this is all there is". */}
        {elsewhere.length > 0 && <Divider />}
        {elsewhere.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 0.5, display: "block", fontWeight: 600 }}>
            Switch workspace
          </Typography>
        )}
        {elsewhere.map((option) => (
          <MenuItem
            key={option.id}
            onClick={() => {
              setAnchor(null);
              switchWorkspace(user, option.id, current, history, switchDatabase);
            }}
          >
            <ListItemIcon>
              <WorkspacesRoundedIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
              {option.name}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

function AccountMenu(props) {
  const history = useHistory();
  const [anchor, setAnchor] = useState(null);
  const [leaving, setLeaving] = useState(false);
  const open = Boolean(anchor);
  const showExplorer = readFeatures().relationshipExplorer && props.rootGUID && typeof props.rootGUID === "string";
  const user = props.user || {};
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ");
  const plan = PLAN_NAMES[user.plan || user.subscription] || user.plan || user.subscription;
  // The workspace this person is working in: named here, so the context is
  // readable on every screen, and switchable where they are in several.
  const workspace = currentWorkspace(user);
  const several = hasSeveralWorkspaces(user);
  const inTrial = workspace?.plan === "trial";
  // ...and the rest of where they are: the library, and the view it is read
  // through. The same three things Home's greeting names, in the same words.
  const database = (props.path || "").replace(/^\//, "") || user.mainDatabase || "";
  // The library's name is for the people the console opens for; everyone else
  // is told the workspace and the view, which is what says what they can see
  // (utils/permissions.js showsLibraryName). The view line below is shown
  // whenever a library is open, not only when its name is - it is the line
  // that carries the context once the name is gone.
  const databaseName = database && showsLibraryName(user) ? user.databaseLabels?.[database] || database : "";
  const view = viewContextLine(useViewConfig()?.view);

  const go = (path) => {
    setAnchor(null);
    history.push(path);
  };

  const logOut = () => {
    recordSignOut();
    localStorage.clear();
    history.push("/login");
    history.go(0);
  };

  return (
    <>
      <ButtonBase
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        sx={{ borderRadius: 999, p: 0.25, "&:focus-visible": { outline: `2px solid ${brand.ink}` } }}
      >
        <Avatar sx={{ width: 36, height: 36, bgcolor: brand.ink, color: brand.signal, fontSize: "0.9rem", fontWeight: 700 }}>
          {initialsOf(user, props.userEmail)}
        </Avatar>
      </ButtonBase>
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ className: "bfx", sx: { mt: 1, minWidth: 250, borderRadius: 3, border: `1px solid ${brand.line}` } }}
      >
        <Box sx={{ px: 2, pt: 1, pb: 1.5 }}>
          {name && <Typography sx={{ fontWeight: 600 }}>{name}</Typography>}
          <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
            {props.userEmail}
          </Typography>
          {workspace?.name && (
            <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
              <WorkspacesRoundedIcon fontSize="small" sx={{ color: brand.signalDeep }} />
              <Typography variant="body2" sx={{ fontWeight: 650, overflowWrap: "anywhere" }}>
                {workspace.name}
              </Typography>
            </Box>
          )}
          {databaseName && (
            <Box sx={{ mt: 0.5, display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
              <StorageRoundedIcon fontSize="small" sx={{ color: "text.secondary" }} />
              <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
                {databaseName}
              </Typography>
            </Box>
          )}
          {/* Which view this library is read through - what a reader used to
              find a plan name in its place, and, for a reader not shown the
              library's name, the line that says where they are. */}
          {database && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block", overflowWrap: "anywhere" }}>
              {view}
            </Typography>
          )}
          {/* The plan is a billing matter, not context: out of the customer's
              UI behind SHOW_PLANS (frontend/src/features.js). */}
          {SHOW_PLANS && plan && (
            <Chip size="small" label={plan} sx={{ mt: 1, bgcolor: brand.signal, color: brand.ink, fontWeight: 600 }} />
          )}
        </Box>
        <Divider />
        {/* Only where there is something to choose between. Switching narrows
            what the screens offer to that workspace; the server still allows
            every database the person may use, in any of their workspaces. */}
        {several && [
          <Typography
            key="workspaces-heading"
            variant="caption"
            color="text.secondary"
            sx={{ px: 2, pt: 1, pb: 0.5, display: "block", fontWeight: 600 }}
          >
            Switch workspace
          </Typography>,
          ...workspacesOf(user).map((option) => (
            <MenuItem
              key={option.id}
              selected={option.id === workspace?.id}
              onClick={() => {
                setAnchor(null);
                if (option.id !== workspace?.id) switchWorkspace(user, option.id, props.path.substring(1), history, switchDatabase);
              }}
            >
              <ListItemIcon>{option.id === workspace?.id ? <CheckRoundedIcon fontSize="small" /> : null}</ListItemIcon>
              <Typography variant="body2" sx={{ fontWeight: option.id === workspace?.id ? 700 : 500, overflowWrap: "anywhere" }}>
                {option.name}
              </Typography>
            </MenuItem>
          )),
          <Divider key="workspaces-divider" />,
        ]}
        <MenuItem onClick={() => go("/my-account")}>
          <ListItemIcon><PersonRoundedIcon fontSize="small" /></ListItemIcon>
          My account
        </MenuItem>
        <MenuItem onClick={() => go("/changepassword")}>
          <ListItemIcon><KeyRoundedIcon fontSize="small" /></ListItemIcon>
          Change password
        </MenuItem>
        {showExplorer && (
          <MenuItem onClick={() => go(`${props.path}/object/${props.rootGUID}`)}>
            <ListItemIcon><HubRoundedIcon fontSize="small" /></ListItemIcon>
            Relationship Explorer
          </MenuItem>
        )}
        {manageableWorkspaces(user).length > 0 && (
          <MenuItem onClick={() => go("/workspace")}>
            <ListItemIcon><WorkspacesRoundedIcon fontSize="small" /></ListItemIcon>
            Manage workspace
          </MenuItem>
        )}
        {SHOW_DEVELOPER_API && (
          <MenuItem onClick={() => go("/developers")}>
            <ListItemIcon><CodeRoundedIcon fontSize="small" /></ListItemIcon>
            Developers
          </MenuItem>
        )}
        {!canPlatform(user, "platform.view") && canOpenConsole(user) && (
          <MenuItem onClick={() => go("/admin/upload-bpmn")}>
            <ListItemIcon><EditNoteRoundedIcon fontSize="small" /></ListItemIcon>
            Edit content
          </MenuItem>
        )}
        {canPlatform(user, "platform.view") && (
          <MenuItem onClick={() => go("/admin")}>
            <ListItemIcon><AdminPanelSettingsRoundedIcon fontSize="small" /></ListItemIcon>
            Admin console
          </MenuItem>
        )}
        <Divider />
        {/* Trial access isn't left: it ends on its own. */}
        {memberWorkspaces(user).length > 0 && workspace && !inTrial && (
          <MenuItem
            onClick={() => {
              setAnchor(null);
              setLeaving(true);
            }}
          >
            <ListItemIcon><ExitToAppRoundedIcon fontSize="small" /></ListItemIcon>
            Leave workspace
          </MenuItem>
        )}
        <MenuItem onClick={logOut} sx={{ color: "error.main" }}>
          <ListItemIcon><LogoutRoundedIcon fontSize="small" sx={{ color: "error.main" }} /></ListItemIcon>
          Log out
        </MenuItem>
      </Menu>
      <ConfirmDialog
        open={leaving}
        title={`Leave ${workspace?.name || "your workspace"}?`}
        body="You lose access to its databases at once. Your account stays; to come back, an admin of the workspace invites you again."
        confirmLabel="Leave workspace"
        onConfirm={async () => {
          const res = await workspacesApi.leave(workspace?.id);
          if (res.success) {
            history.push("/home");
            history.go(0);
          }
          return res;
        }}
        onClose={() => setLeaving(false)}
      />
    </>
  );
}

const RelationshipExplorerSearch = (props) => {
  const { results, searching, search } = useQuickSearch();
  const history = useHistory();

  return (
    <InputGroup className="bf-search-group">
      <Typeahead
        // The server has already matched the term; keep every result.
        filterBy={() => true}
        clearButton={false}
        id="relationship-search"
        labelKey="name"
        options={results}
        isLoading={searching}
        placeholder="Search the relationship explorer"
        onInputChange={search}
        emptyLabel="Nothing found in this database"
        renderMenuItemChildren={(option) => (
          <div onClick={() => props.setNodeDatum({
            id: option.guid, guid: option.guid,
            shortName: getShortName(option.name), fullName: option.name,
            type: option.type, description: option.description,
            children: [], isLink: false, style: { background: "#FFFFFF" },
          })}>
            {option.name}
            <div>
              <small>
                <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: option.colour, marginRight: 6 }} />
                {option.typeLabel}
              </small>
            </div>
            {option.detail && (
              <div>
                <small className="text-muted">{option.detail}</small>
              </div>
            )}
          </div>
        )}
        positionFixed
        className="bf-search-typeahead"
      />
      {/* Only where Advanced search is offered (Console → Features): the
          button must never point at a screen that redirects home. */}
      {readFeatures().advanceSearch && (
        <Button
          className="bf-advanced-btn"
          onClick={() => history.push(`${props.path}/advance-search`)}
          variant="link"
        >
          Advanced
        </Button>
      )}
    </InputGroup>
  );
};

const ExtendedSearch = (props) => {
  const { results, searching, search } = useQuickSearch();
  const history = useHistory();

  return (
    <InputGroup className="bf-search-group">
      <Typeahead
        // The server has already matched the term; keep every result.
        filterBy={() => true}
        clearButton={false}
        id="main-search"
        labelKey="name"
        options={results}
        isLoading={searching}
        placeholder="Search processes"
        onInputChange={search}
        emptyLabel="Nothing found in this database"
        renderMenuItemChildren={(option) => (
          <div onClick={() => history.push(`${props.path}/map/${option.diagramOf || option.guid}`)}>
            {option?.name}
            <div>
              <small>
                <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: option.colour, marginRight: 6 }} />
                {option.typeLabel}
              </small>
            </div>
            {option.detail && (
              <div>
                <small className="text-muted">{option.detail}</small>
              </div>
            )}
          </div>
        )}
        positionFixed
        className="bf-search-typeahead"
      />
      {/* Only where Advanced search is offered (Console → Features): the
          button must never point at a screen that redirects home. */}
      {readFeatures().advanceSearch && (
        <Button
          className="bf-advanced-btn"
          onClick={() => history.push(`${props.path}/advance-search`)}
          variant="link"
        >
          Advanced
        </Button>
      )}
    </InputGroup>
  );
};

export default Navbar;
